(() => {
  if (customElements.get('tl-wishlist-button')) return;
  const config = JSON.parse(document.getElementById('TrendyLeslieFeatures')?.textContent || '{}');
  const root = config.root || '/';
  const storageKey = 'trendy-leslie:wishlist:v1';
  const heart = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" focusable="false"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z"/></svg>';
  const productCache = new Map();
  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(storageKey) || '[]');
      return Array.isArray(value) ? [...new Set(value.filter(h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,254}$/i.test(h)))].slice(0, 100) : [];
    } catch { return []; }
  };
  const announce = message => {
    const status = document.getElementById('tl-feature-status');
    if (status) status.textContent = message;
  };
  const notify = () => document.dispatchEvent(new CustomEvent('tl:wishlist-change'));
  const formatMoney = (cents, currency = config.currency || 'EUR') => new Intl.NumberFormat(config.locale || 'nl', { style: 'currency', currency }).format(cents / 100);
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  };
  const productUrl = handle => `${root}products/${encodeURIComponent(handle)}`;
  const loadProduct = handle => {
    if (!productCache.has(handle)) {
      const promise = fetch(`${productUrl(handle)}.js`).then(response => {
        if (!response.ok) throw new Error(response.status === 404 ? 'unavailable' : 'network');
        return response.json();
      }).catch(error => { productCache.delete(handle); throw error; });
      productCache.set(handle, promise);
    }
    return productCache.get(handle);
  };
  window.addEventListener('storage', event => { if (event.key === storageKey || event.key === null) notify(); });

  customElements.define('tl-wishlist-button', class extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        const handles = read();
        const exists = handles.includes(this.dataset.handle);
        const updated = exists ? handles.filter(h => h !== this.dataset.handle) : [this.dataset.handle, ...handles].slice(0, 100);
        try { localStorage.setItem(storageKey, JSON.stringify(updated)); }
        catch { announce(config.storage_error); return; }
        announce(exists ? config.removed : config.saved);
        notify();
      }, options);
      document.addEventListener('tl:wishlist-change', () => this.sync(), options);
      this.sync();
    }
    sync() {
      const saved = read().includes(this.dataset.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute('aria-label', saved ? this.dataset.removeLabel : this.dataset.saveLabel);
      const label = this.button.querySelector('span');
      if (label) label.textContent = saved ? config.saved_button : config.save;
    }
    disconnectedCallback() { this.controller?.abort(); }
  });

  customElements.define('tl-wishlist-count', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      document.addEventListener('tl:wishlist-change', () => this.sync(), { signal: this.controller.signal });
      this.sync();
    }
    sync() { const count = read().length; this.textContent = count; this.hidden = count === 0; }
    disconnectedCallback() { this.controller?.abort(); }
  });

  const wishlistButton = (handle, title) => {
    const control = node('tl-wishlist-button', 'tl-wishlist');
    control.dataset.handle = handle;
    control.dataset.saveLabel = config.save_label.replace('[product]', title);
    control.dataset.removeLabel = config.remove_label.replace('[product]', title);
    const button = node('button');
    button.type = 'button';
    button.innerHTML = heart;
    control.append(button);
    return control;
  };

  const cardGallery = (link, product) => {
    const sources = [product.featured_image, ...(product.images || [])].filter(Boolean);
    const keys = new Set();
    const photos = sources.filter(source => {
      const key = new URL(source, location.origin).pathname;
      if (keys.has(key)) return false;
      keys.add(key);
      return true;
    }).map(source => {
      const resize = width => {
        const url = new URL(source, location.origin);
        url.searchParams.set('width', String(width));
        return url.href;
      };
      return { src: resize(1200), srcset: [360, 540, 720, 960, 1200].map(width => `${resize(width)} ${width}w`).join(','), alt: product.title, width: 640, height: 800 };
    });
    if (photos.length < 2) return link;
    const gallery = node('chastar-card-gallery');
    gallery.style.setProperty('--cc-fit', 'contain');
    gallery.setAttribute('role', 'group');
    gallery.setAttribute('aria-label', config.gallery_label.replace('[product]', product.title));
    gallery.dataset.positionLabel = config.gallery_position;
    gallery.dataset.errorLabel = config.gallery_error;
    link.setAttribute('data-cpg-viewport', '');
    const data = node('script');
    data.type = 'application/json';
    data.setAttribute('data-cpg-images', '');
    data.textContent = JSON.stringify(photos);
    gallery.append(link, data);
    for (const step of [-1, 1]) {
      const button = node('button', `cpg__arrow cpg__arrow--${step < 0 ? 'previous' : 'next'}`);
      button.type = 'button';
      button.dataset.cpgStep = String(step);
      button.setAttribute('data-cpg-controls', '');
      button.hidden = true;
      button.setAttribute('aria-label', (step < 0 ? config.gallery_previous : config.gallery_next).replace('[product]', product.title));
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '1.5');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', step < 0 ? 'm14 6-6 6 6 6' : 'm10 6 6 6-6 6');
      svg.append(path); button.append(svg); gallery.append(button);
    }
    const position = node('span', 'cpg__position', `1 / ${photos.length}`);
    position.setAttribute('data-cpg-position', '');
    position.setAttribute('data-cpg-controls', '');
    position.setAttribute('aria-hidden', 'true');
    position.hidden = true;
    const status = node('span', 'cpg__status');
    status.setAttribute('data-cpg-status', '');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    gallery.append(position, status);
    return gallery;
  };

  customElements.define('tl-wishlist-page', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.grid = this.querySelector('[data-wishlist-grid]');
      document.addEventListener('tl:wishlist-change', () => this.render(), { signal: this.controller.signal });
      this.render();
    }
    async render() {
      const sequence = this.sequence = (this.sequence || 0) + 1;
      const handles = read();
      this.querySelector('[data-wishlist-empty]').hidden = handles.length > 0;
      const loading = this.querySelector('[data-wishlist-loading]');
      loading.hidden = handles.length === 0;
      this.grid.replaceChildren();
      let next = 0;
      const cards = handles.map(handle => {
        const card = node('article', 'tl-wishlist-card');
        card.setAttribute('aria-busy', 'true');
        card.dataset.handle = handle;
        this.grid.append(card);
        return card;
      });
      const worker = async () => {
        while (next < handles.length && sequence === this.sequence && this.isConnected) {
          const index = next++;
          const handle = handles[index];
          const card = cards[index];
          try {
            const product = await loadProduct(handle);
            if (sequence !== this.sequence || !this.isConnected) return;
            const visual = node('div', 'tl-wishlist-card__visual');
            const link = node('a', 'tl-wishlist-card__image');
            link.href = productUrl(handle);
            link.setAttribute('aria-label', product.title);
            if (product.featured_image) {
              const image = node('img');
              const imageUrl = new URL(product.featured_image, location.origin);
              imageUrl.searchParams.set('width', '640');
              image.src = imageUrl.href;
              image.alt = product.title;
              image.width = 640; image.height = 800; image.loading = 'lazy'; image.decoding = 'async';
              link.append(image);
            }
            visual.append(cardGallery(link, product), wishlistButton(handle, product.title));
            const heading = node('h2');
            const titleLink = node('a', '', product.title);
            titleLink.href = productUrl(handle); heading.append(titleLink);
            const price = node('p', 'tl-wishlist-card__price', formatMoney(product.price));
            const action = node('a', 'tl-text-link', product.available ? config.view : config.unavailable);
            action.href = productUrl(handle);
            card.replaceChildren(visual, heading, price, action);
          } catch (error) {
            if (sequence !== this.sequence || !this.isConnected) return;
            card.append(wishlistButton(handle, handle), node('p', '', error.message === 'unavailable' ? config.unavailable : config.load_error));
            if (error.message !== 'unavailable') {
              const retry = node('button', 'tl-text-link', config.retry);
              retry.type = 'button'; retry.addEventListener('click', () => this.render(), { once: true }); card.append(retry);
            }
          }
          card.removeAttribute('aria-busy');
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, handles.length) }, worker));
      if (sequence === this.sequence) loading.hidden = true;
    }
    disconnectedCallback() { this.controller?.abort(); ++this.sequence; }
  });

  customElements.define('tl-cart-complement', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.show !== 'true' || !this.dataset.productId) return;
      this.controller = new AbortController();
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      });
      this.observer.observe(this);
    }
    async load() {
      try {
        const url = new URL(`${this.dataset.recommendationsUrl}.json`, location.origin);
        url.searchParams.set('product_id', this.dataset.productId);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'complementary');
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) return;
        const result = await response.json();
        const cartProducts = JSON.parse(this.dataset.cartProducts || '[]').map(String);
        const product = result.products?.find(p => p.available && !cartProducts.includes(String(p.id)));
        if (product && this.isConnected) this.showProduct(product);
      } catch {}
    }
    showProduct(product) {
      const card = this.querySelector('.tl-complement');
      const link = node('a', 'tl-complement__image');
      link.href = product.url || productUrl(product.handle);
      link.setAttribute('aria-label', product.title);
      if (product.featured_image) {
        const image = node('img');
        const imageUrl = new URL(product.featured_image, location.origin);
        imageUrl.searchParams.set('width', '180');
        image.src = imageUrl.href; image.alt = product.title; image.width = 90; image.height = 112; image.loading = 'lazy'; image.decoding = 'async';
        link.append(image);
      }
      const details = node('div');
      details.append(node('p', 'tl-eyebrow', config.complement));
      const title = node('a', 'tl-complement__title', product.title);
      title.href = link.href;
      const action = node('a', 'tl-text-link', config.choose);
      action.href = link.href;
      details.append(title, node('p', 'tl-complement__price', formatMoney(product.price, this.dataset.currency)), action);
      card.replaceChildren(link, details);
      card.hidden = false;
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); }
  });
})();
